import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

const postsSlice = createSlice({
  name: "posts",
  initialState: {
    posts: [],
    topTags: [],
    filterTopTags: [],
    status: "idle",
    error: [],
    loading: false, // New loading state
    currentPage: -1,
    arePostsLeft: true,
    count_loading: {},
  },
  reducers: {
    setLoading: (state) => {
      state.status = "loading";
      state.loading = true; // Set loading to true
    },
    setCountLoading: (state, action) => {
      const { postId, loading } = action.payload;
      state.count_loading[postId] = loading; // Set loading state per post
    },
    resetPosts: (state) => {
      state.posts = [];
      state.currentPage = -1;
      state.arePostsLeft = true;
    },
    setArePostsLeft: (state, action) => {
      state.status = "succeeded";
      state.arePostsLeft = action.payload;
    },
    setFetchingPostsLoading: (state) => {
      state.status = "fetching";
    },
    incrementCurrentPage: (state) => {
      state.currentPage += 1;
    },
    setPosts: (state, action) => {
      state.status = "succeeded";
      state.loading = false; // Set loading to false
      state.posts = state.posts.concat(action.payload);
    },
    setTopTags: (state, action) => {
      state.status = "succeeded";
      state.loading = false; // Set loading to false
      state.topTags = action.payload;
      state.filterTopTags = action.payload;
    },
    searchFilter: (state, action) => {
      const searchText = action.payload.toLowerCase();
      if (!searchText) {
        state.filterTopTags = state.topTags; // Reset to original when input is empty
      } else {
        state.filterTopTags = state.topTags.filter((item) =>
          item.value.toLowerCase().includes(searchText)
        );
      }
    },

    setTagFilte: (state, action) => {
      state.status = "failed";
      state.loading = false; // Set loading to false
      state.error = action.payload;
    },
    setError: (state, action) => {
      state.status = "failed";
      state.loading = false; // Set loading to false
      state.error = action.payload;
    },
    removeTopic: (state, action) => {
      state.status = "succeeded";
      state.loading = false; // Ensure loading is false
      state.posts = state.posts.filter((topic) => topic.id !== action.payload);
    },

    addTopic: (state, action) => {
      state.status = "succeeded";
      state.loading = false; // Set loading to false
      state.posts.unshift({
        ...action.payload,
        id: action.payload.topic_id,
        title: action.payload.category.topic_title,
      });
    },
    resetError: (state) => {
      state.status = "idle";
      state.loading = false; // Reset loading to false
      state.error = null;
    },
    appendPost: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      state.posts.push(action.payload);
    },
    toggleBookmark: (state, action) => {
      state.status = "succeeded";
      state.loading = false;
      const { postId, bookmarked, bookmark_id } = action.payload;
      state.posts = state.posts.map((post) => {
        return post?.first_post_details?.post_id === postId
          ? {
              ...post,
              bookmarked,
              first_post_details: {
                ...post?.first_post_details,
                ...(bookmarked && { bookmark_id }),
                is_post_bookmarked:
                  !post?.first_post_details?.is_post_bookmarked,
              },
            }
          : post;
      });
    },
    toggleLike: (state, action) => {
      const { postId, isLiked } = action.payload;

      state.posts = state.posts.map((post) => {
        if (post?.first_post_details?.post_id === postId) {
          const currentLikeCount = post?.first_post_details?.like_count || 0;
          return {
            ...post,
            first_post_details: {
              ...post.first_post_details,
              is_post_liked: isLiked,
              like_count: isLiked ? currentLikeCount + 1 : currentLikeCount - 1,
            },
          };
        }
        return post;
      });
    },
  },
});

export const {
  setLoading,
  setPosts,
  setError,
  removeTopic,
  addTopic,
  resetError,
  setPostReplies,
  appendPost,
  toggleBookmark,
  toggleLike,
  setTopTags,
  setFetchingPostsLoading,
  setArePostsLeft,
  incrementCurrentPage,
  resetPosts,
  searchFilter,
  setCountLoading,
} = postsSlice.actions;

export const fetchPosts =
  (method = "latest") =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading());
      dispatch(setFetchingPostsLoading());
      dispatch(incrementCurrentPage());
      const state = getState();
      const { currentPage } = state.posts;

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/latest.json?no_definitions=true&page=${currentPage}${
          method === "new"
            ? ""
            : method === "solved"
            ? "&solved=yes"
            : "&order=views&ascending=false&include_display_username=true"
        }`
      );

      const posts = response.data.topic_list.topics.filter(
        (topic) => !topic.closed
      );
      if (posts.length === 0) {
        dispatch(setArePostsLeft(false));
      } else dispatch(setPosts(posts));
    } catch (err) {
      dispatch(setError(err.message));
    }
  };

export const fetchCategoryPosts =
  (category, method, solved = undefined) =>
  async (dispatch, getState) => {
    try {
      dispatch(setLoading());
      dispatch(setFetchingPostsLoading());
      dispatch(incrementCurrentPage());

      let solve =
        solved !== undefined ? `&solved=${solved ? "yes" : "no"}` : "";
      const state = getState();
      const { currentPage } = state.posts;

      const response = await axios.get(
        `${
          process.env.REACT_APP_API_URL
        }/c/${category}/l/latest.json?no_definitions=true&page=${currentPage}${
          method === "new"
            ? ""
            : `&order=views&ascending=false${solve ? solve : ""}`
        }`
      );
      const posts = response.data.topic_list.topics.filter(
        (topic) => !topic.closed
      );

      if (posts.length === 0) {
        dispatch(setArePostsLeft(false));
      } else {
        dispatch(setPosts(posts));
        dispatch(
          setTopTags(
            response.data.topic_list.top_tags.map((tag) => ({
              label: tag,
              value: tag,
            }))
          )
        );
      }
    } catch (err) {
      dispatch(setError(err.message));
    }
  };

export const deleteTopic = (id) => async (dispatch) => {
  try {
    dispatch(setLoading());

    await axios.delete(`${process.env.REACT_APP_API_URL}/t/${id}.json`);
    dispatch(removeTopic(id));
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export const createTopic = (post) => async (dispatch) => {
  try {
    await dispatch(setLoading());
    const formData = new FormData();
    formData.append("title", post?.title);
    formData.append("raw", post?.raw);
    formData.append("category", post?.category);
    post?.tag.forEach((tag) => {
      formData.append("tags[]", tag.id);
    });

    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/posts.json?include_topic_title`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    await dispatch(addTopic(response.data));
    return response.data;
  } catch (err) {
    console.error("Error Response:", err.response?.data);
    dispatch(setError(err.response?.data?.errors));
  }
};

export const toggleBookmarkPost = (post) => async (dispatch) => {
  try {
    let postId = post?.first_post_details?.post_id;
    if (!post?.bookmarked) {
      //not bookmarked previously
      const formData = new FormData();
      formData.append("auto_delete_preference", 3);
      formData.append("bookmarkable_id", postId);
      formData.append("bookmarkable_type", "Post");
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/bookmarks/`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      const { success, id } = response.data;

      if (success) {
        dispatch(toggleBookmark({ postId, bookmarked: true, bookmark_id: id }));
      }
    } else {
      let bookmark_id = post?.first_post_details?.bookmark_id;

      const response = await axios.delete(
        `${process.env.REACT_APP_API_URL}/bookmarks/${bookmark_id}.json`
      );
      const { success } = response.data;
      if (success) {
        dispatch(toggleBookmark({ postId, bookmarked: false }));
      }
    }
  } catch (err) {
    console.log(err);
    dispatch(setError(err));
  }
};

export const togglePostReaction = (postId, isLiked) => async (dispatch) => {
  try {
    dispatch(setCountLoading({ postId, loading: true }));
    const url = `${process.env.REACT_APP_API_URL}/discourse-reactions/posts/${postId}/custom-reactions/heartpulse/toggle.json`;
    const response = await axios.put(url, {});

    if (!response.data.errors) {
      dispatch(toggleLike({ postId, isLiked }));
    }
  } catch (err) {
    console.error("Error toggling reaction:", err);
    throw err; // This ensures React reverts UI on failure
  } finally {
    dispatch(setCountLoading({ postId, loading: false }));
  }
};
export const fetchSpecifcTopicAndItsPosts = (topicId) => async (dispatch) => {
  try {
    dispatch(setLoading());
    if (!topicId) {
      dispatch(setError(["Topic id is required"]));
      return;
    }
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/t/${topicId}/1.json`
    );
    const posts = response.data.topic_list.topics;
    dispatch(setPosts(posts));
  } catch (err) {
    dispatch(setError(err.response.data.errors));
  }
};

export const fetchPostReplies = (postId) => async (dispatch) => {
  try {
    dispatch(setLoading());
    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/posts/${postId}/replies`
    );
    const replies = response.data;
    dispatch(setPostReplies(replies));
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export const fetchPostsByTags = (categoryId, tags) => async (dispatch) => {
  try {
    dispatch(setLoading());

    let tagString = "";
    tags.forEach((tag, index) => {
      tagString += `tag_names[]=${tag.value}`;
      if (index !== tags.length - 1) {
        tagString += "&";
      }
    });

    const response = await axios.get(
      `${process.env.REACT_APP_API_URL}/tags/c/${categoryId}/abc/l/latest.json?${tagString}`
    );
    const posts = response.data.topic_list.topics.filter(
      (topic) => !topic.closed
    );
    dispatch(setPosts(posts));
  } catch (err) {
    dispatch(setError(err.message));
  }
};

export default postsSlice.reducer;
